import { GridDataService } from './grid-data.service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { MessageHandlerService } from '../../utility/message-handler.service';
import { remove } from 'lodash';
import { ColumnSettings } from '../../model/column-settings';
import { GridQueryUtils } from '../../utility/grid-query-utils';

@Injectable()
export class CaseListService extends GridDataService
{
    public translateService: TranslateService;

    constructor(http: HttpClient, translate: TranslateService, messageHandlerService: MessageHandlerService)
    {
        super(http, '/cases', translate, messageHandlerService);
        this.translateService = translate;
    }

    public setDefaultColumnsSetting(filterState: string, filterColumns: string, defaultPageSize: number, patientLabel: string,
        communityEnabled: boolean = null): Promise<any[]>
    {
        return new Promise((resolve: any) =>
        {
            this.translateService.get(['MEMBER', 'CHANNEL', 'PROGRAM', 'CASE-MANAGER', 'REFERRED-ORGANIZATION', 'REFERRED-SERVICE',
                'REFERRAL-FROM', 'CHANNEL-WORKFLOW-STATUS', 'DATE-OPENED', 'LAST-ACTIVITY', 'CASE-ID-NUMBER', 'CASE-STATUS', 'PRIORITY',
                'CASE-FLAGS', 'CLOSED-ON'])
                .subscribe({
                    next: (res) =>
                    {
                        const columnsConfig: ColumnSettings[] = [
                            {
                                field: 'name',
                                title: res.MEMBER,
                                titleKeys: ['MEMBER'],
                                width: 18,
                                sortable: true,
                                filterable: true,
                                hidden: false,
                                class: 'font-weight-bold emphasizedLabel'
                            },
                            {
                                field: 'channel',
                                title: res.CHANNEL,
                                titleKeys: ['CHANNEL'],
                                width: 20,
                                sortable: true,
                                filterable: true,
                                hidden: false,
                                class: 'ellipsis-text'
                            },
                            {
                                field: 'channel.program',
                                title: res.PROGRAM,
                                titleKeys: ['PROGRAM'],
                                width: 20,
                                sortable: true,
                                filterable: true,
                                hidden: false
                            },
                            {
                                field: 'manager',
                                title: res['CASE-MANAGER'],
                                titleKeys: ['CASE-MANAGER'],
                                width: 17,
                                sortable: true,
                                filterable: true,
                                hidden: false
                            },
                            {
                                field: 'eventReferral.referredToOrganizationName',
                                title: res['REFERRED-ORGANIZATION'],
                                titleKeys: ['REFERRED-ORGANIZATION'],
                                width: 15,
                                sortable: true,
                                filterable: true,
                                hidden: false
                            },
                            {
                                field: 'eventReferral.referredServiceName',
                                title: res['REFERRED-SERVICE'],
                                titleKeys: ['REFERRED-SERVICE'],
                                width: 15,
                                sortable: true,
                                filterable: true,
                                hidden: true
                            },
                            {
                                field: 'eventReferral.referredFromOrganizationName',
                                title: res['REFERRAL-FROM'],
                                titleKeys: ['REFERRAL-FROM'],
                                width: 15,
                                sortable: true,
                                filterable: true,
                                hidden: false
                            },
                            {
                                field: 'channelWorkflowStatus.displayName',
                                title: res['CHANNEL-WORKFLOW-STATUS'],
                                titleKeys: ['CHANNEL-WORKFLOW-STATUS'],
                                width: 13,
                                sortable: true,
                                filterable: true,
                                hidden: false
                            },
                            {
                                field: 'openedOn',
                                title: res['DATE-OPENED'],
                                titleKeys: ['DATE-OPENED'],
                                width: 20,
                                sortable: true,
                                filterable: true,
                                hidden: false,
                                filter: 'date'
                            },
                            {
                                field: 'history.lastActivityOn',
                                title: res['LAST-ACTIVITY'],
                                titleKeys: ['LAST-ACTIVITY'],
                                width: 20,
                                sortable: true,
                                filterable: true,
                                hidden: false,
                                filter: 'date'
                            },
                            {
                                field: 'id',
                                title: res['CASE-ID-NUMBER'],
                                titleKeys: ['CASE-ID-NUMBER'],
                                width: 15,
                                filter: 'numeric',
                                format: '{0:n}',
                                sortable: true,
                                filterable: true,
                                hidden: false
                            },
                            {
                                field: 'status',
                                title: res['CASE-STATUS'],
                                titleKeys: ['CASE-STATUS'],
                                width: 12,
                                sortable: true,
                                filterable: true,
                                hidden: false,
                                columnMenu: false
                            },
                            {
                                field: 'priority',
                                title: res.PRIORITY,
                                titleKeys: ['PRIORITY'],
                                width: 12,
                                columnMenu: false,
                                sortable: true,
                                filterable: true,
                                hidden: false,
                                class: 'text-center'
                            },
                            {
                                field: 'caseFlagsText',
                                title: res['CASE-FLAGS'],
                                titleKeys: ['CASE-FLAGS'],
                                width: 50,
                                sortable: true,
                                filterable: true,
                                hidden: true
                            },
                            {
                                field: 'closedOn',
                                title: res['CLOSED-ON'],
                                titleKeys: ['CLOSED-ON'],
                                width: 80,
                                sortable: true,
                                filterable: true,
                                hidden: true,
                                filter: 'date',
                                format: '{0:d}'
                            },
                            {
                                field: 'compositeRiskScore',
                                title: res['COMPOSITE-RISK-SCORE'],
                                titleKeys: ['COMPOSITE-RISK-SCORE'],
                                width: 25,
                                filter: 'numeric',
                                format: '{0:n}',
                                sortable: true,
                                filterable: true,
                                hidden: true,
                                class: 'text-center'
                            }
                        ];

                        // Set grid columns
                        const loadedColumns = GridQueryUtils.setBaseColumnsSetting(this.translateService,
                            filterColumns, columnsConfig, patientLabel);

                        // Removed columns if option not enabled (for setting edit, communityEnabled is null)
                        if (communityEnabled !== null && !communityEnabled)
                        {
                            remove(loadedColumns, (x: ColumnSettings) =>
                                x.field === 'eventReferral.referredToOrganizationName' || x.field === 'eventReferral.referredServiceName' ||
                                x.field === 'eventReferral.referredFromOrganizationName');
                        }

                        resolve({
                            state: filterState ? JSON.parse(filterState) : { skip: 0, group: [], sort: [], take: defaultPageSize },
                            columns: loadedColumns
                        });
                    }
                });
        });
    }
}
